import { type ReactElement, type ReactNode, useMemo } from 'react';
import {
  type GridCellParams,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridRowId,
  type GridRowParams,
  type GridSortModel,
  type GridValidRowModel,
} from '@/ui/primitives';
import { DataGrid, type DataGridProps } from '../primitives/DataGrid';
import { DataTableShell } from './DataTableShell';
import { useServerDataGridState } from './grid/useServerDataGridState';
import { applyColumnWidths, wrapDataGridColumns } from './grid/wrapDataGridColumns';
import type { GridQueryInput, GridQueryResult } from './grid/gridUtils';
import type { QueryKey } from '@tanstack/react-query';

type BaseDataTableProps = {
  title?: string;
  actions?: ReactNode;
  state?: 'ready' | 'loading' | 'empty' | 'error';
  emptyTitle?: string;
  emptyBody?: string;
  errorTitle?: string;
  errorBody?: string;
  errorAction?: ReactNode;
};

type ServerDataTableProps<T extends GridValidRowModel> = {
  queryPath: string;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  columns: Array<GridColDef<T>>;
  getRowId: (row: T) => GridRowId;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  height?: number;
  noRowsText?: string;
  filteredNoRowsText?: string;
  onRowsChange?: (rows: T[]) => void;
  onQueryMeta?: (meta: { total: number; items: T[] } | null) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryKeyPrefix?: QueryKey;
  queryKey?: QueryKey;
  onRefetch?: (refetch: () => Promise<void>) => void;
  onInvalidate?: (invalidate: () => Promise<void>) => void;
  onRowClick?: (params: GridRowParams<T>) => void;
  onCellClick?: (params: GridCellParams<T>) => void;
  onColumnHeaderClick?: DataGridProps['onColumnHeaderClick'];
  onQueryInput?: (input: GridQueryInput) => void;
  isAuthenticated?: boolean;
  organisationId?: string | null;
  projectId?: string | null;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  autoHeight?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  getRowHeight?: DataGridProps['getRowHeight'];
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
  refetchIntervalMs?: number | false;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  gridProps?: DataGridProps;
};

type DataTableProps<T extends GridValidRowModel> =
  | (BaseDataTableProps & { gridProps: DataGridProps; server?: undefined })
  | (BaseDataTableProps & { server: ServerDataTableProps<T>; gridProps?: DataGridProps });

const DataTableGrid = ({
  title,
  actions,
  state,
  emptyTitle,
  emptyBody,
  errorTitle,
  errorBody,
  errorAction,
  gridProps,
}: BaseDataTableProps & { gridProps: DataGridProps }): ReactElement => {
  const wrappedColumns = useMemo(
    () => wrapDataGridColumns([...gridProps.columns]),
    [gridProps.columns],
  );
  const resolvedGridProps = { ...gridProps, columns: wrappedColumns };
  const grid = <DataGrid {...resolvedGridProps} />;
  const showShell = Boolean(title) || Boolean(actions);

  if (!showShell) {
    return grid;
  }

  return (
    <DataTableShell
      title={title}
      actions={actions}
      state={state}
      emptyTitle={emptyTitle}
      emptyBody={emptyBody}
      errorTitle={errorTitle}
      errorBody={errorBody}
      errorAction={errorAction}
    >
      {grid}
    </DataTableShell>
  );
};

const DataTableServer = <T extends GridValidRowModel>({
  title,
  actions,
  state = 'ready',
  emptyTitle,
  emptyBody,
  errorTitle,
  errorBody,
  errorAction,
  server,
  gridProps,
}: BaseDataTableProps & {
  server: ServerDataTableProps<T>;
  gridProps?: DataGridProps;
}): ReactElement => {
  const {
    queryPath,
    queryFn,
    columns,
    getRowId,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText,
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel,
    initialFilterModel,
    queryKeyPrefix,
    queryKey,
    onRefetch,
    onInvalidate,
    onRowClick,
    onCellClick,
    onColumnHeaderClick,
    onQueryInput,
    isAuthenticated = true,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl,
    autoHeight,
    disableColumnMenu = true,
    showToolbar = false,
    getRowHeight,
    columnVisibilityModel,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    gridProps: serverGridProps,
  } = server;

  const wrappedColumns = useMemo(() => wrapDataGridColumns(columns), [columns]);
  const {
    errorMessage,
    rowsData,
    rowCount,
    queryIsFetching,
    resolvedPageSizeOptions,
    resolvedNoRowsText,
    paginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    captureScrollAnchor,
    filterModel,
    setFilterModel,
    columnVisibilityModel: visibilityModel,
    setColumnVisibilityModel,
    columnWidthModel,
    handleColumnWidthChange,
  } = useServerDataGridState<T>({
    queryPath,
    queryFn,
    columns: wrappedColumns,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText,
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel,
    initialFilterModel,
    queryKeyPrefix,
    queryKey,
    onRefetch,
    onInvalidate,
    onQueryInput,
    isAuthenticated,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl,
    autoHeight,
    columnVisibilityModel,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    getRowHeight,
  });

  const columnsWithWidths = useMemo(
    () => applyColumnWidths(columns, columnWidthModel),
    [columnWidthModel, columns],
  );
  const resolvedEmptyTitle = emptyTitle ?? resolvedNoRowsText;
  const mergedProps: DataGridProps = {
    rows: rowsData,
    columns: columnsWithWidths,
    loading: queryIsFetching,
    disableRowSelectionOnClick: true,
    rowSelection: false,
    disableColumnMenu,
    hideFooterSelectedRowCount: true,
    pageSizeOptions: resolvedPageSizeOptions,
    paginationMode: 'server',
    sortingMode: 'server',
    filterMode: 'server',
    paginationModel,
    onPaginationModelChange: handlePaginationModelChange,
    rowCount,
    sortModel,
    onSortModelChange: handleSortModelChange,
    onColumnHeaderClick: (...args) => {
      captureScrollAnchor();
      onColumnHeaderClick?.(...args);
    },
    filterModel,
    onFilterModelChange: setFilterModel,
    columnVisibilityModel: visibilityModel,
    onColumnVisibilityModelChange: setColumnVisibilityModel,
    onColumnWidthChange: handleColumnWidthChange,
    onRowClick,
    onCellClick,
    getRowId,
    autoHeight,
    getRowHeight,
    showToolbar,
    emptyTitle: resolvedEmptyTitle,
    emptyBody,
    error: Boolean(errorMessage),
    errorTitle: errorMessage ?? errorTitle,
    errorBody,
  };

  const resolvedGridProps = serverGridProps
    ? { ...mergedProps, ...serverGridProps }
    : gridProps
      ? { ...mergedProps, ...gridProps }
      : mergedProps;

  return (
    <DataTableGrid
      title={title}
      actions={actions}
      state={state}
      emptyTitle={emptyTitle}
      emptyBody={emptyBody}
      errorTitle={errorTitle}
      errorBody={errorBody}
      errorAction={errorAction}
      gridProps={resolvedGridProps}
    />
  );
};

export const DataTable = <T extends GridValidRowModel>(props: DataTableProps<T>): ReactElement => {
  if ('server' in props && props.server) {
    return <DataTableServer {...props} />;
  }

  return <DataTableGrid {...props} />;
};
